// NOTE: if you add any filters that rely on model ids (for ex. product.segmentId, product.recommenderId, product.campaignId, etc.)
// don't forget to change domain-migration-helper in the API to replace source ids with destination ids in segment.rules[].filters[]!
const filters = {
  count: {
    name: 'Count',
    inputType: 'range',
    conditionType: 'count',
    min: 1,
    step: 1,
    placeholder: ['e.g. 1', 'e.g. 3'],
  },
  // Sums `points` across the events its parent rule matches - it does NOT compute a
  // net balance, so it must never be labelled "available". Two labels because the
  // same aggregation means different things under the two loyalty rules.
  totalLoyaltyPoints: {
    name: 'Earned loyalty points',
    field: 'custom.numeric.points',
    inputType: 'range',
    conditionType: 'floatRange',
    placeholder: ['e.g. 50', 'e.g. 99'],
    aggregation: 'sum',
  },
  totalLoyaltyPointsRedeemed: {
    name: 'Redeemed loyalty points',
    field: 'custom.numeric.points',
    inputType: 'range',
    conditionType: 'floatRange',
    placeholder: ['e.g. 50', 'e.g. 99'],
    aggregation: 'sum',
  },
  group: {
    name: 'Group',
    field: 'group',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    step: 1,
    max: 9999,
    placeholder: ['starting from 0', 'up to and including 9999'],
  },
  tags: {
    name: 'Tags',
    field: 'tags',
    inputType: 'string',
    conditionType: 'term',
  },
  locale: {
    name: 'Locale',
    field: 'locale',
    inputType: 'string',
    conditionType: 'term',
  },
  subscriptions: {
    name: 'Subscriptions',
    field: 'subscriptions',
    inputType: 'select',
    conditionType: 'term',
  },
  relativeTimeframe: {
    name: 'Days ago',
    field: 'timestamp',
    inputType: 'range',
    conditionType: 'relativeTimeRange',
    min: 0,
    step: 1,
    placeholder: ['e.g. 5', 'e.g. 0'],
  },
  relativeTimeframeHours: {
    name: 'Hours ago',
    field: 'timestamp',
    inputType: 'range',
    conditionType: 'relativeTimeRangeHours',
    min: 0,
    step: 1,
    placeholder: ['e.g. 5', 'e.g. 0'],
  },
  absoluteTimeframe: {
    name: 'Time',
    field: 'timestamp',
    inputType: 'dateRange',
    conditionType: 'absoluteTimeRange',
  },
  price: {
    name: 'Price',
    field: 'products.price',
    inputType: 'range',
    conditionType: 'floatRange',
    placeholder: ['e.g. 50', 'e.g. 99'],
  },
  discountPercent: {
    name: 'Discount %',
    field: 'products.discountPercent',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    placeholder: ['e.g. 1', 'e.g. 20'],
  },
  discount: {
    name: 'Discount amount',
    field: 'products.discount',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    placeholder: ['e.g. 50', 'e.g. 99'],
  },
  cartTotal: {
    name: 'Cart total',
    field: 'cartTotalInDomainCurrency',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    placeholder: ['e.g. 50', 'e.g. 99'],
  },
  scrollPercent: {
    name: 'Scroll depth %',
    field: 'scrollPercent',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    max: 100,
    step: 1,
    placeholder: ['e.g. 25', 'e.g. 100'],
  },
  activeTimeMs: {
    name: 'Time on page (milliseconds)',
    field: 'activeTimeMs',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    step: 1000,
    placeholder: ['e.g. 5000', 'e.g. 60000'],
  },
  categories: {
    name: 'Categories',
    field: 'products.categories',
    inputType: 'string',
    conditionType: 'term',
  },
  surveys: {
    name: 'Survey name',
    field: 'custom.string.surveyId',
    inputType: 'select',
    conditionType: 'term',
  },
  city: {
    name: 'City',
    field: 'city',
    inputType: 'string',
    conditionType: 'term',
  },
  region: {
    name: 'Region',
    field: 'region',
    inputType: 'string',
    conditionType: 'term',
  },
  country: {
    name: 'Country code',
    field: 'country',
    inputType: 'string',
    conditionType: 'term',
  },
  campaignId: {
    name: 'After campaign / workflow interaction',
    field: 'products.campaignId',
    inputType: 'select',
    conditionType: 'term',
  },
  emailId: {
    name: 'After email interaction',
    field: 'products.emailId',
    inputType: 'select',
    conditionType: 'term',
  },
  pushNotificationId: {
    name: 'After push notification interaction',
    field: 'products.pushNotificationId',
    inputType: 'select',
    conditionType: 'term',
  },
  textMessageId: {
    name: 'After text message interaction',
    field: 'products.textMessageId',
    inputType: 'select',
    conditionType: 'term',
  },
  viberMessageId: {
    name: 'After viber message interaction',
    field: 'products.viberMessageId',
    inputType: 'select',
    conditionType: 'term',
  },
  // Values are recommender tokens. Domain cloning does NOT remap them yet (see the NOTE at the
  // top of this file and the same gap on `pageToken`) - until domain-migration-helper in the API
  // handles both, a cloned segment carrying this filter keeps source-domain tokens and silently
  // matches nothing. Tracked in https://github.com/Releva-ai/magellan-api/pull/2763.
  productBlock: {
    name: 'After product block interaction',
    field: 'products.recommenderId',
    inputType: 'select',
    conditionType: 'term',
  },
  bannerBlock: {
    name: 'After banner block interaction',
    field: 'products.bannerBlockId',
    inputType: 'select',
    conditionType: 'term',
  },
  action: {
    name: 'Action name',
    field: 'action',
    inputType: 'select',
    conditionType: 'term',
  },
  eventTags: {
    name: 'Tags',
    field: 'tags',
    inputType: 'select',
    conditionType: 'term',
  },
  campaignTags: {
    name: 'After campaign/workflow with tags',
    field: 'products.campaignTags',
    inputType: 'select',
    conditionType: 'term',
  },
  utmSource: {
    name: 'Utm by Source',
    field: 'products.utmSource',
    inputType: 'string',
    conditionType: 'term',
  },
  utmMedium: {
    name: 'Utm by Medium',
    field: 'products.utmMedium',
    inputType: 'string',
    conditionType: 'term',
  },
  utmCampaign: {
    name: 'Utm by Campaign',
    field: 'products.utmCampaign',
    inputType: 'string',
    conditionType: 'term',
  },
  productId: {
    name: 'Product Id',
    field: 'products.id',
    inputType: 'string',
    conditionType: 'term',
  },
  firstName: {
    name: 'First name (lowercase)',
    field: 'firstName',
    inputType: 'string',
    conditionType: 'term',
  },
  lastName: {
    name: 'Last name (lowercase)',
    field: 'lastName',
    inputType: 'string',
    conditionType: 'term',
  },
  consent: {
    name: 'Consent',
    field: 'consent',
    inputType: 'string',
    conditionType: 'term',
  },
  emailDomain: {
    name: 'Email domain (e.g. abv.bg)',
    field: 'emailDomain',
    inputType: 'string',
    conditionType: 'term',
  },
  pageUrl: {
    name: 'Page Url',
    field: 'url',
    inputType: 'string',
    conditionType: 'term',
  },
  page: {
    name: 'Page',
    field: 'pageToken',
    inputType: 'select',
    conditionType: 'term',
  },
  deviceType: {
    name: 'Device type',
    field: 'userAgent->tags',
    inputType: 'select',
    conditionType: 'term',
  },
  orderId: {
    name: 'Order Id',
    field: 'orderId',
    inputType: 'string',
    conditionType: 'term',
    values: ['{{triggerEvent.orderId}}'],
  },
  shipmentStatus: {
    name: 'Shipment Status',
    field: 'shipmentStatus',
    inputType: 'select',
    conditionType: 'term',
  },
  availability: {
    name: 'Available / unavailable',
    field: 'products.available',
    inputType: 'checkbox',
    conditionType: 'term',
    values: [true],
  },
  segmentId: {
    name: 'Segment',
    field: 'segmentId',
    inputType: 'select',
    conditionType: 'segment',
  },
  daysUntilBirthday: {
    name: 'Days until birthday',
    field: 'birthday',
    inputType: 'number',
    min: 0,
    max: 180,
    step: 1,
    conditionType: 'daysUntilBirthday',
    placeholder: '0',
  },
  recencyScore: {
    name: 'Recency Score',
    field: 'rfmRecency',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 1,
    max: 5,
    step: 1,
    placeholder: ['1', '5'],
  },
  frequencyScore: {
    name: 'Frequency Score',
    field: 'rfmFrequency',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 1,
    max: 5,
    step: 1,
    placeholder: ['1', '5'],
  },
  monetaryScore: {
    name: 'Monetary Score',
    field: 'rfmValue',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 1,
    max: 5,
    step: 1,
    placeholder: ['1', '5'],
  },
  churnProbabilityScore: {
    name: 'Churn Probability Score',
    field: 'churnProbability',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    max: 1,
    placeholder: ['0', '1'],
  },
  retargetPredictionScore: {
    name: 'Retarget Prediction Score',
    field: 'retargetPrediction',
    inputType: 'range',
    conditionType: 'floatRange',
    min: 0,
    max: 1,
    placeholder: ['0', '1'],
  },
  lifeTimeValue: {
    name: 'Life Time Value',
    field: 'ltv',
    inputType: 'range',
    conditionType: 'floatRange',
    placeholder: ['e.g. 50', 'e.g. 99'],
  },
  observedLifeTimeValue: {
    name: 'Observed Life Time Value',
    field: 'observedLtv',
    inputType: 'range',
    conditionType: 'floatRange',
    placeholder: ['e.g. 50', 'e.g. 99'],
  },
  whatsappMessageId: {
    name: 'After whatsapp message interaction',
    field: 'products.whatsappMessageId',
    inputType: 'select',
    conditionType: 'term',
  },
  webhookId: {
    name: 'Webhook',
    field: 'products.webhookId',
    inputType: 'select',
    conditionType: 'term',
  },
  hasEmail: {
    name: 'Registered Email',
    field: 'email',
    inputType: 'checkbox',
    conditionType: 'exists',
    values: [true],
  },
  hasPhoneNumber: {
    name: 'Registered Phone Number',
    field: 'phoneNumber',
    inputType: 'checkbox',
    conditionType: 'exists',
    values: [true],
  },
};

export default filters;
